import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Constant from "../Utils/Constant";
import BackendConnector from '../Utils/BackendConnector';

const GallerySection = () => {
    const [categories, setCategories] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [albums, setAlbums] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // Compteur « Voir plus » par catégorie : {[categoryId]: count}
    const [displayedCounts, setDisplayedCounts] = useState({});
    const navigate = useNavigate();

    const handleTabClick = (categoryId, event) => {
        event.preventDefault();
        setActiveId(categoryId);
        // Réinitialiser le compteur d'affichage quand on change d'onglet
        setDisplayedCounts(prev => ({ ...prev, [categoryId]: Constant.ITEMS_PER_PAGE }));
    };

    const handleAlbumClick = (albumId) => {
        navigate(`/album/${albumId}`);
    };

    const handleShowMore = (categoryId) => {
        setDisplayedCounts(prev => ({
            ...prev,
            [categoryId]: (prev[categoryId] || Constant.ITEMS_PER_PAGE) + Constant.ITEMS_PER_PAGE,
        }));
    };

    const LoadingSkeleton = () => (
        <div className="row">
            {[...Array(Constant.ITEMS_PER_PAGE)].map((_, index) => (
                <div key={index} className="col-lg-3 col-md-4 col-sm-6 col-12 mb-4">
                    <div className="album-card loading">
                        <div className="album-image-container">
                            <div className="loading-shimmer">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Chargement...</span>
                                </div>
                            </div>
                        </div>
                        <div className="album-overlay">
                            <div className="album-info">
                                <div className="placeholder-glow">
                                    <span className="placeholder col-8" style={{height: '1.2rem', borderRadius: '4px'}}></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            BackendConnector.fetchData('categories/'),
            BackendConnector.fetchData('albums/'),
        ])
            .then(([cats, albumList]) => {
                setCategories(cats);
                setAlbums(albumList);
                setActiveId(cats.length > 0 ? cats[0].id : null);
                setIsLoading(false);
            })
            .catch(error => {
                // Repli : aucune catégorie => pas d'onglets, état « aucun album »
                console.error('Erreur lors du chargement de la galerie:', error);
                setIsLoading(false);
            });
    }, []);

    const categoryAlbums = albums.filter(album => album.category === activeId);
    const displayedCount = displayedCounts[activeId] || Constant.ITEMS_PER_PAGE;

    return (
        <section id="gallery" className="section">
            <h2>{Constant.GALLERY}</h2>
            <ul className="nav nav-tabs" role="tablist">
                {categories.map(category => (
                    <li key={category.id} className="nav-item" role="presentation">
                        <button
                            className={`nav-link ${activeId === category.id ? 'active' : ''}`}
                            id={`category-${category.id}-tab`}
                            type="button"
                            role="tab"
                            aria-controls={`category-${category.id}`}
                            aria-selected={activeId === category.id}
                            onClick={(e) => handleTabClick(category.id, e)}
                        >
                            {category.name}
                        </button>
                    </li>
                ))}
            </ul>

            <div className="tab-content mt-3">
                <div
                    className="tab-pane fade show active"
                    id={`category-${activeId}`}
                    role="tabpanel"
                    aria-labelledby={`category-${activeId}-tab`}
                >
                    {isLoading ? (
                        <LoadingSkeleton />
                    ) : categoryAlbums.length === 0 ? (
                        <div className="row">
                            <div className="col-12">
                                <div className="no-albums-message">
                                    <div className="no-albums-icon">📸</div>
                                    <h3>{Constant.NO_ALBUMS_MESSAGE}</h3>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="row">
                                {categoryAlbums.slice(0, displayedCount).map(album => (
                                    <div key={album.id} className="col-lg-3 col-md-4 col-sm-6 col-12 mb-4">
                                        <div className="album-card" onClick={() => handleAlbumClick(album.id)}>
                                            <div className="album-image-container">
                                                {album.cover_thumb || album.cover ? (
                                                    <img
                                                        src={album.cover_thumb || album.cover}
                                                        alt={album.title}
                                                        className="album-image"
                                                    />
                                                ) : (
                                                    <div className="album-placeholder">
                                                        <i className="fas fa-mountain" style={{fontSize: '2rem', color: '#6c757d'}}></i>
                                                        <p className="text-muted mb-0 mt-2">{Constant.NO_IMAGE_MESSAGE}</p>
                                                    </div>
                                                )}
                                                <div className="album-overlay">
                                                    <div className="album-info">
                                                        <h5 className="album-title">{album.title}</h5>
                                                        <div className="album-meta">
                                                            <span className="album-type">{album.category_name}</span>
                                                            {album.country && <span className="album-country">📍 {album.country}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="album-action">
                                                        <button className="btn-view">{Constant.SEE_ALBUM}</button>
                                                    </div>
                                                </div>
                                                <div className="album-title-permanent">
                                                    <h6 className="permanent-title">{album.title}</h6>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {categoryAlbums.length > displayedCount && (
                                <div className="row">
                                    <div className="col-12 text-center mt-4">
                                        <button
                                            className="btn-view"
                                            onClick={() => handleShowMore(activeId)}
                                        >
                                            {Constant.SHOW_MORE_ALBUMS}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}

export default GallerySection;
